import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { dayText } from "./accounts";
import { callTime, emailName } from "./calls";
import { CHECK_IN_COLUMNS, CONTRACT_VERSION_COLUMNS, DOCUMENT_COLUMNS, RENEWAL_COLUMNS } from "./columns";
import {
  checkInProblem,
  COMMERCIAL_TEXT,
  fileProblem,
  inWindow,
  KIND_TEXT,
  KINDS,
  leftText,
  ratingText,
  readWindow,
  replacedOn,
  sha256Hex,
  sizeText,
  termsProblem,
  type TermsField,
  type Window,
  windowCounts,
  windowText,
} from "./contracts";
import { type Method, METHOD_TEXT, money, rebateShort, rebateText, TERMS } from "./deals";
import { go, useTitle } from "./hooks";
import { todayIn } from "./pickup";
import { type Saved, SERVER_PROBLEM, saveFailure } from "./save";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { Skeleton } from "./Skeleton";

type Failure = "missing" | "server" | "network" | "session";

const failureOf = (e: unknown): Failure => {
  const code = e instanceof api.ApiError ? e.status : 0;
  return code === 404 ? "missing" : code === 401 ? "session" : code >= 500 ? "server" : "network";
};

function Status({ saved, auth, testId }: { saved: Saved | null; auth: Auth; testId: string }) {
  return (
    <p role="status" data-testid={testId} className={saved && !saved.ok ? "error" : undefined}>
      {saved?.text}
      {saved && !saved.ok && saved.session && (
        <>
          {" "}
          <button type="button" onClick={() => signOut(auth)}>
            Sign in again
          </button>
        </>
      )}
    </p>
  );
}

/** The added consideration: the text, the amount, or both. */
const considerationText = (c: api.Contract) =>
  [c.consideration, c.consideration_amount ? money(c.consideration_amount) : ""].filter(Boolean).join(", ");

const renewsText = (c: { auto_renew: boolean; notice_days: number }) => (c.auto_renew ? `Yes, ${c.notice_days} days notice` : "No");

/** Design 9.7. The API sends this section only to the roles with the contract grant (Q-14). */
export function ContractSection({
  account,
  org,
  auth,
  user,
  onSaved,
}: {
  account: api.AccountDetail;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  onSaved: (next: api.AccountDetail) => void;
}) {
  const contract = account.contract;
  const [changing, setChanging] = useState<"change" | "renew" | null>(null);
  const [done, setDone] = useState<string | null>(null);
  if (!contract) return null;
  const can = Boolean(account.can_change_contract);
  return (
    <section aria-labelledby="account-contract" data-testid="contract-section">
      <h2 id="account-contract">Contract</h2>
      <dl className="facts">
        <dt>Version</dt>
        <dd data-testid="contract-version">{contract.version}</dd>
        <dt>Term</dt>
        <dd data-testid="contract-term">
          {contract.term_months} months, ends {dayText(contract.ends_on, true)}
          {contract.auto_renew ? ` · renews automatically, ${contract.notice_days} days notice` : " · no automatic renewal"}
        </dd>
        <dt>This version</dt>
        <dd>From {dayText(contract.starts_on, true)}</dd>
        <dt>Rebate</dt>
        <dd data-testid="contract-rebate">{rebateText(contract.rebate_method, contract.rebate_value)}</dd>
        {considerationText(contract) && (
          <>
            <dt>Added consideration</dt>
            <dd>{considerationText(contract)}</dd>
          </>
        )}
        <dt>Payee</dt>
        <dd>
          {contract.payee_name}
          {contract.payee_address && <span className="sub"> · {contract.payee_address}</span>}
        </dd>
        <dt>Signed</dt>
        <dd>
          {contract.signed_on ? dayText(contract.signed_on, true) : "–"}
          {contract.signer_name && ` by ${contract.signer_name}${contract.signer_title ? `, ${contract.signer_title}` : ""}`}
        </dd>
        <dt>Salesperson</dt>
        <dd>{contract.salesperson ?? "House account"}</dd>
      </dl>
      {can && changing === null && (
        <p className="actions">
          <button
            type="button"
            data-testid="contract-change"
            onClick={() => {
              setDone(null);
              setChanging("change");
            }}
          >
            Change the terms
          </button>
          <button
            type="button"
            data-testid="contract-renew"
            onClick={() => {
              setDone(null);
              setChanging("renew");
            }}
          >
            Renew
          </button>
        </p>
      )}
      <p role="status" data-testid="contract-status">
        {done}
      </p>
      {can && changing !== null && (
        <TermsForm
          key={changing}
          account={account}
          contract={contract}
          renew={changing === "renew"}
          org={org}
          auth={auth}
          user={user}
          onClose={() => setChanging(null)}
          onSaved={(next) => {
            onSaved(next);
            setChanging(null);
            const saved = next.contract;
            if (saved) setDone(`Saved: version ${saved.version}, from ${dayText(saved.starts_on, true)}.`);
          }}
        />
      )}
      {(account.contract_versions?.length ?? 0) > 1 && <VersionTable versions={account.contract_versions ?? []} org={org} />}
      <Documents account={account} org={org} auth={auth} user={user} onSaved={onSaved} />
      <CheckIns account={account} org={org} auth={auth} user={user} onSaved={onSaved} />
    </section>
  );
}

function VersionTable({ versions, org }: { versions: api.Contract[]; org: api.Bootstrap }) {
  const [sort, setSort] = useSort("vsort", CONTRACT_VERSION_COLUMNS);
  return (
    <>
      <h3 id="contract-versions">Versions</h3>
      <SortSelect id="versions-select" columns={CONTRACT_VERSION_COLUMNS} sort={sort} onSort={setSort} fallback="Newest first" />
      <div className="table-scroll" tabIndex={0} role="region" aria-labelledby="contract-versions">
        <table className="list" data-testid="version-table" data-cols="versions-select">
          <thead>
            <tr>
              {CONTRACT_VERSION_COLUMNS.map((column) => (
                <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
              ))}
            </tr>
          </thead>
          <tbody>
            {sortRows(versions, CONTRACT_VERSION_COLUMNS, sort).map((version) => (
              <tr key={version.id} data-testid="version-row">
                <th scope="row">{version.version}</th>
                <td data-label="Starts">{dayText(version.starts_on, true)}</td>
                <td data-label="Ends">{replacedOn(versions, version) ? `Replaced on ${dayText(replacedOn(versions, version) ?? "", true)}` : dayText(version.ends_on, true)}</td>
                <td data-label="Term">{version.term_months} months</td>
                <td data-label="Rebate">{rebateText(version.rebate_method, version.rebate_value)}</td>
                <td data-label="Renews">{renewsText(version)}</td>
                <td data-label="Added consideration">{considerationText(version) || "–"}</td>
                <td data-label="Payee">{version.payee_name}</td>
                <td data-label="Recorded" title={version.recorded_email ?? undefined}>
                  {callTime(version.recorded_at, org.time_zone)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** The rebate as the user types it: a percent shows as 50 for 0.5. */
const shownRebate = (method: string, value: number) => (method === "percent" ? String(Math.round(value * 10_000) / 100) : String(value));
const storedRebate = (method: string, text: string) => (method === "percent" ? Number(text) / 100 : Number(text));

function TermsForm({
  account,
  contract,
  renew,
  org,
  auth,
  user,
  onClose,
  onSaved,
}: {
  account: api.AccountDetail;
  contract: api.Contract;
  renew: boolean;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  onClose: () => void;
  onSaved: (next: api.AccountDetail) => void;
}) {
  const today = todayIn(org.time_zone);
  const dayAfterEnd = new Date(`${contract.ends_on}T12:00:00Z`);
  dayAfterEnd.setUTCDate(dayAfterEnd.getUTCDate() + 1);
  const [startsOn, setStartsOn] = useState(renew ? dayAfterEnd.toISOString().slice(0, 10) : today);
  const [method, setMethod] = useState(contract.rebate_method);
  const [value, setValue] = useState(shownRebate(contract.rebate_method, contract.rebate_value));
  const [term, setTerm] = useState(contract.term_months);
  const [autoRenew, setAutoRenew] = useState(contract.auto_renew);
  const [notice, setNotice] = useState(String(contract.notice_days));
  const [consideration, setConsideration] = useState(contract.consideration);
  const [amount, setAmount] = useState(contract.consideration_amount ? String(contract.consideration_amount) : "");
  const [payee, setPayee] = useState(contract.payee_name);
  const [payeeAddress, setPayeeAddress] = useState(contract.payee_address);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const [wrong, setWrong] = useState<{ field: TermsField; text: string } | null>(null);
  const entryId = useRef<string | null>(null);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => first.current?.focus(), []);
  const edit = (next: () => void) => {
    next();
    entryId.current = null;
    setSaved(null);
    setWrong(null);
  };
  const marked = (field: TermsField) =>
    wrong?.field === field ? { "aria-invalid": true, "aria-describedby": `terms-${field}-error` } : {};
  const fieldError = (field: TermsField) =>
    wrong?.field === field && (
      <p id={`terms-${field}-error`} role="alert" className="error" data-testid="terms-field-error">
        {wrong.text}
      </p>
    );
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const rebate = storedRebate(method, value);
    if (!startsOn) return setSaved({ ok: false, text: "Enter the first day of the new version." });
    if (!Number.isFinite(rebate) || rebate < 0) return setSaved({ ok: false, text: "Enter a rebate of 0 or more." });
    const problem = termsProblem({ payee, payeeAddress, consideration, consideration_amount: amount, notice });
    if (problem) {
      setWrong(problem);
      document.getElementById(`terms-${problem.field}`)?.focus();
      return;
    }
    entryId.current ??= crypto.randomUUID();
    const change: api.ContractChange = {
      id: entryId.current,
      starts_on: startsOn,
      renew,
      rebate_method: method,
      rebate_value: rebate,
      term_months: term,
      auto_renew: autoRenew,
      notice_days: Number(notice),
      consideration: consideration.trim(),
      consideration_amount: amount.trim() === "" ? 0 : Number(amount),
      payee_name: payee.trim(),
      payee_address: payeeAddress.trim(),
    };
    setSaving(true);
    setSaved(null);
    try {
      onSaved(await api.changeContract(await user.getIdToken(), account.id, change));
    } catch (e) {
      setSaved(saveFailure(e, "The new version"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} noValidate data-testid="terms-form" aria-labelledby="terms-form-title">
      <h3 id="terms-form-title">{renew ? "Renew the contract" : "Change the terms"}</h3>
      <p className="hint">
        {renew
          ? "A renewal is a new version with a new term. The old versions stay."
          : "A change is a new version from the first day below. The old versions stay."}
      </p>
      <label htmlFor="terms-starts">First day</label>
      <input ref={first} id="terms-starts" data-testid="terms-starts" type="date" value={startsOn} onChange={(e) => edit(() => setStartsOn(e.target.value))} />
      <label htmlFor="terms-method">Rebate method</label>
      <select
        id="terms-method"
        data-testid="terms-method"
        value={method}
        onChange={(e) =>
          edit(() => {
            setValue(shownRebate(e.target.value, storedRebate(method, value)));
            setMethod(e.target.value);
          })
        }
      >
        {(Object.keys(METHOD_TEXT) as Method[]).map((key) => (
          <option key={key} value={key}>
            {METHOD_TEXT[key]}
          </option>
        ))}
      </select>
      <label htmlFor="terms-rebate">Rebate{method === "percent" ? " (%)" : " ($)"}</label>
      <input id="terms-rebate" data-testid="terms-rebate" inputMode="decimal" value={value} onChange={(e) => edit(() => setValue(e.target.value))} />
      <label htmlFor="terms-term">Term</label>
      <select id="terms-term" data-testid="terms-term" value={term} onChange={(e) => edit(() => setTerm(Number(e.target.value)))}>
        {TERMS.map((months) => (
          <option key={months} value={months}>
            {months} months
          </option>
        ))}
      </select>
      <label className="check">
        <input type="checkbox" data-testid="terms-auto-renew" checked={autoRenew} onChange={(e) => edit(() => setAutoRenew(e.target.checked))} />
        <span>Renews automatically</span>
      </label>
      <label htmlFor="terms-notice">Notice before the end (days)</label>
      <input id="terms-notice" data-testid="terms-notice" inputMode="numeric" value={notice} {...marked("notice")} onChange={(e) => edit(() => setNotice(e.target.value))} />
      {fieldError("notice")}
      <label htmlFor="terms-consideration">Added consideration</label>
      <input id="terms-consideration" data-testid="terms-consideration" value={consideration} {...marked("consideration")} onChange={(e) => edit(() => setConsideration(e.target.value))} />
      {fieldError("consideration")}
      <label htmlFor="terms-amount">Added consideration amount ($)</label>
      <input id="terms-amount" data-testid="terms-consideration-amount" inputMode="decimal" value={amount} {...marked("amount")} onChange={(e) => edit(() => setAmount(e.target.value))} />
      {fieldError("amount")}
      <label htmlFor="terms-payee">Payee</label>
      <input id="terms-payee" value={payee} {...marked("payee")} onChange={(e) => edit(() => setPayee(e.target.value))} />
      {fieldError("payee")}
      <label htmlFor="terms-payeeAddress">Payee mail address</label>
      <input id="terms-payeeAddress" value={payeeAddress} {...marked("payeeAddress")} onChange={(e) => edit(() => setPayeeAddress(e.target.value))} />
      {fieldError("payeeAddress")}
      <div className="actions">
        <button className="primary" data-testid="terms-save" disabled={saving}>
          {saving ? "Saving…" : renew ? "Save the renewal" : "Save the new version"}
        </button>
        <button type="button" onClick={onClose}>
          Cancel
        </button>
      </div>
      <Status saved={saved} auth={auth} testId="terms-status" />
    </form>
  );
}

function Documents({
  account,
  org,
  auth,
  user,
  onSaved,
}: {
  account: api.AccountDetail;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  onSaved: (next: api.AccountDetail) => void;
}) {
  const [sort, setSort] = useSort("dsort", DOCUMENT_COLUMNS);
  const [problem, setProblem] = useState<string | null>(null);
  const list = account.documents ?? [];
  const download = async (id: string) => {
    setProblem(null);
    try {
      window.location.assign(await api.documentUrl(await user.getIdToken(), id));
    } catch (e) {
      setProblem(saveFailure(e, "The download").text);
    }
  };
  return (
    <>
      <h3 id="contract-documents">Documents</h3>
      {list.length === 0 ? (
        <p data-testid="documents-none">No documents yet.</p>
      ) : (
        <>
          <SortSelect id="documents-select" columns={DOCUMENT_COLUMNS} sort={sort} onSort={setSort} fallback="Newest first" />
          <div className="table-scroll" tabIndex={0} role="region" aria-labelledby="contract-documents">
            <table className="list" data-testid="document-table" data-cols="documents-select">
              <thead>
                <tr>
                  {DOCUMENT_COLUMNS.map((column) => (
                    <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortRows(list, DOCUMENT_COLUMNS, sort).map((document) => (
                  <tr key={document.id} data-testid="document-row">
                    <th scope="row">
                      <button type="button" className="link clip name" data-testid="document-download" title={document.file_name} onClick={() => download(document.id)}>
                        {document.file_name}
                      </button>
                    </th>
                    <td data-label="Kind">{KIND_TEXT[document.kind]}</td>
                    <td className="num" data-label="Size">{sizeText(document.size_bytes)}</td>
                    <td data-label="Added">{document.stored_at ? callTime(document.stored_at, org.time_zone) : "–"}</td>
                    <td data-label="By" title={document.uploaded_email ?? undefined}>
                      {document.uploaded_email ? emailName(document.uploaded_email) : "–"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {problem && (
        <p role="alert" className="error">
          {problem}
        </p>
      )}
      {account.can_change_contract && <UploadForm account={account} auth={auth} user={user} onSaved={onSaved} />}
    </>
  );
}

function UploadForm({
  account,
  auth,
  user,
  onSaved,
}: {
  account: api.AccountDetail;
  auth: Auth;
  user: User;
  onSaved: (next: api.AccountDetail) => void;
}) {
  const [kind, setKind] = useState<api.DocumentKind>("agreement");
  const [file, setFile] = useState<File | null>(null);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const entryId = useRef<string | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const problem = fileProblem(file);
    if (problem || !file) return setSaved({ ok: false, text: problem ?? "Choose a PDF file." });
    entryId.current ??= crypto.randomUUID();
    setSaving(true);
    setSaved({ ok: true, text: "Sending the file…" });
    try {
      const token = await user.getIdToken();
      const entry: api.DocumentEntry = {
        id: entryId.current,
        kind,
        file_name: file.name,
        size_bytes: file.size,
        sha256: await sha256Hex(await file.arrayBuffer()),
      };
      const ticket = await api.startDocument(token, account.id, entry);
      await api.uploadFile(ticket, file);
      onSaved(await api.documentStored(await user.getIdToken(), ticket.id));
      entryId.current = null;
      setFile(null);
      if (input.current) input.current.value = "";
      setSaved({ ok: true, text: `Saved: ${file.name}.` });
    } catch (e) {
      setSaved(saveFailure(e, "The file"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} noValidate data-testid="upload-form" aria-labelledby="upload-form-title">
      <h4 id="upload-form-title">Add a document</h4>
      <label htmlFor="upload-kind">Kind</label>
      <select id="upload-kind" data-testid="upload-kind" value={kind} onChange={(e) => setKind(e.target.value as api.DocumentKind)}>
        {KINDS.map((value) => (
          <option key={value} value={value}>
            {KIND_TEXT[value]}
          </option>
        ))}
      </select>
      <label htmlFor="upload-file">PDF file, 10 MB at most</label>
      <input
        ref={input}
        id="upload-file"
        data-testid="upload-file"
        type="file"
        accept="application/pdf,.pdf"
        onChange={(e) => {
          setFile(e.target.files?.[0] ?? null);
          entryId.current = null;
          setSaved(null);
        }}
      />
      <div className="actions">
        <button data-testid="upload-save" disabled={saving}>
          {saving ? "Sending…" : "Add the document"}
        </button>
      </div>
      <Status saved={saved} auth={auth} testId="upload-status" />
    </form>
  );
}

function CheckIns({
  account,
  org,
  auth,
  user,
  onSaved,
}: {
  account: api.AccountDetail;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  onSaved: (next: api.AccountDetail) => void;
}) {
  const [sort, setSort] = useSort("ksort", CHECK_IN_COLUMNS);
  const list = account.check_ins ?? [];
  const versions = account.contract_versions ?? [];
  const newRebate = (id: string) => {
    const version = versions.find((v) => v.id === id);
    return version ? ` · new rebate ${rebateShort(version.rebate_method, version.rebate_value)}` : " · new rebate";
  };
  return (
    <>
      <h3 id="contract-check-ins">Check-ins</h3>
      {list.length === 0 ? (
        <p data-testid="check-ins-none">No check-ins yet.</p>
      ) : (
        <>
          <SortSelect id="check-ins-select" columns={CHECK_IN_COLUMNS} sort={sort} onSort={setSort} fallback="Newest first" />
          <div className="table-scroll" tabIndex={0} role="region" aria-labelledby="contract-check-ins">
            <table className="list" data-testid="check-in-table" data-cols="check-ins-select">
              <thead>
                <tr>
                  {CHECK_IN_COLUMNS.map((column) => (
                    <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortRows(list, CHECK_IN_COLUMNS, sort).map((item) => (
                  <tr key={item.id} data-testid="check-in-row" data-day={item.met_on}>
                    <th scope="row">{dayText(item.met_on, true)}</th>
                    <td data-label="Rating">{ratingText(item.rating)}</td>
                    <td data-label="Contact" title={item.contact || undefined}>
                      <span className="clip name">{item.contact || "–"}</span>
                    </td>
                    <td data-label="Result" title={item.result}>
                      <span className="clip name">{item.result || "–"}</span>
                      {item.contract_version_id && <span className="sub">{newRebate(item.contract_version_id)}</span>}
                    </td>
                    <td data-label="Next check-in">{item.next_on ? dayText(item.next_on, true) : "–"}</td>
                    <td data-label="By" title={item.recorded_email ?? undefined}>
                      {item.recorded_email ? emailName(item.recorded_email) : "–"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {account.can_change_contract && <CheckInForm account={account} org={org} auth={auth} user={user} onSaved={onSaved} />}
    </>
  );
}

function CheckInForm({
  account,
  org,
  auth,
  user,
  onSaved,
}: {
  account: api.AccountDetail;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  onSaved: (next: api.AccountDetail) => void;
}) {
  const today = todayIn(org.time_zone);
  const contract = account.contract;
  const [metOn, setMetOn] = useState(today);
  const [contact, setContact] = useState("");
  const [rating, setRating] = useState(0);
  const [result, setResult] = useState("");
  const [nextOn, setNextOn] = useState("");
  const [rebateChange, setRebateChange] = useState(false);
  const [rebate, setRebate] = useState(contract ? shownRebate(contract.rebate_method, contract.rebate_value) : "");
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const entryId = useRef<string | null>(null);
  const edit = (next: () => void) => {
    next();
    entryId.current = null;
    setSaved(null);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const problem = checkInProblem({ metOn, nextOn, rating, today, contact, result });
    if (problem) return setSaved({ ok: false, text: problem });
    entryId.current ??= crypto.randomUUID();
    const method = contract?.rebate_method ?? "rate";
    const entry: api.CheckInEntry = {
      id: entryId.current,
      met_on: metOn,
      contact: contact.trim(),
      rating,
      result: result.trim(),
      next_on: nextOn,
      rebate: rebateChange ? { rebate_method: method, rebate_value: storedRebate(method, rebate) } : null,
    };
    setSaving(true);
    setSaved(null);
    try {
      onSaved(await api.addCheckIn(await user.getIdToken(), account.id, entry));
      entryId.current = null;
      setContact("");
      setResult("");
      setRating(0);
      setNextOn("");
      setRebateChange(false);
      setSaved({ ok: true, text: `Saved: check-in of ${dayText(metOn, true)}, ${ratingText(rating)}.` });
    } catch (e) {
      setSaved(saveFailure(e, "The check-in"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} noValidate data-testid="check-in-form" aria-labelledby="check-in-form-title">
      <h4 id="check-in-form-title">Record a check-in</h4>
      <label htmlFor="check-in-day">Day</label>
      <input id="check-in-day" data-testid="check-in-day" type="date" max={today} value={metOn} onChange={(e) => edit(() => setMetOn(e.target.value))} />
      <label htmlFor="check-in-contact">Contact</label>
      <input id="check-in-contact" data-testid="check-in-contact" value={contact} onChange={(e) => edit(() => setContact(e.target.value))} />
      <fieldset className="rating">
        <legend>Rating</legend>
        {[1, 2, 3, 4, 5].map((value) => (
          <label key={value}>
            <input type="radio" name="check-in-rating" data-testid={`check-in-rating-${value}`} checked={rating === value} onChange={() => edit(() => setRating(value))} /> {value}
          </label>
        ))}
      </fieldset>
      <label htmlFor="check-in-result">Result</label>
      <textarea id="check-in-result" data-testid="check-in-result" rows={2} value={result} onChange={(e) => edit(() => setResult(e.target.value))} />
      <label htmlFor="check-in-next">Next check-in</label>
      <input id="check-in-next" data-testid="check-in-next" type="date" min={metOn} value={nextOn} onChange={(e) => edit(() => setNextOn(e.target.value))} />
      {contract && (
        <>
          <label className="check">
            <input type="checkbox" data-testid="check-in-rebate-change" checked={rebateChange} onChange={(e) => edit(() => setRebateChange(e.target.checked))} /> The rebate changes from this day
          </label>
          {rebateChange && (
            <>
              <label htmlFor="check-in-rebate">New rebate{contract.rebate_method === "percent" ? " (%)" : " ($)"}</label>
              <input id="check-in-rebate" data-testid="check-in-rebate" inputMode="decimal" value={rebate} onChange={(e) => edit(() => setRebate(e.target.value))} />
            </>
          )}
        </>
      )}
      <div className="actions">
        <button data-testid="check-in-save" disabled={saving}>
          {saving ? "Saving…" : "Save the check-in"}
        </button>
      </div>
      <Status saved={saved} auth={auth} testId="check-in-status" />
    </form>
  );
}

/** The commercial status and, when it does not agree with the service status, the reason (design 9.6). */
export function CommercialFact({ account }: { account: api.Account }) {
  if (!account.commercial_status) return null;
  return (
    <>
      <dt>Commercial status</dt>
      <dd data-testid="account-commercial">
        {COMMERCIAL_TEXT[account.commercial_status]}
        {account.mismatch && (
          <>
            {" "}
            <span className="chip-flag" data-testid="account-mismatch">
              Status mismatch
            </span>
            <span className="sub" data-testid="account-mismatch-reason">
              {account.mismatch}
            </span>
          </>
        )}
      </dd>
    </>
  );
}

type RenewalLoad = api.RenewalList | Failure | "hidden" | undefined;

/** Design 9.7 and spec F-SAL-8: the contracts that end soon, and the ended ones. */
export function RenewalList({ base, org, auth, user, canView }: { base: string; org: api.Bootstrap; auth: Auth; user: User; canView: boolean | undefined }) {
  const [found, setFound] = useState<RenewalLoad>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [chosen, setChosen] = useState<Window>("all");
  const [sort, setSort] = useSort("sort", RENEWAL_COLUMNS);
  useTitle(`Renewals · ${org.name}`);
  useEffect(() => {
    if (canView !== true) return;
    let live = true;
    user
      .getIdToken()
      .then(api.renewals)
      .then((list) => {
        if (!live) return;
        setFound(list);
        setChosen(readWindow(location.search, list.windows));
      })
      .catch((e) => {
        const failure = failureOf(e);
        if (live) setFound(failure === "missing" ? "hidden" : failure);
      });
    return () => {
      live = false;
    };
  }, [user, attempt, canView]);
  if (canView === false || found === "hidden") {
    return (
      <main data-testid="renewals-hidden">
        <h1 tabIndex={-1}>Renewals</h1>
        <p>Your role does not show contracts.</p>
      </main>
    );
  }
  const pick = (next: Window) => {
    setChosen(next);
    const url = new URL(location.href);
    if (next === "all") url.searchParams.delete("window");
    else url.searchParams.set("window", String(next));
    history.replaceState(history.state, "", url);
  };
  const list = typeof found === "object" ? found : null;
  const shown = list ? list.renewals.filter((row) => inWindow(row, chosen)) : [];
  return (
    <main className="renewals-page wide" data-testid="renewals-page">
      <h1 tabIndex={-1}>Renewals</h1>
      <p className="sub">
        The newest contract version of each account that ends in 180 days or less, and the contracts that ended. The list starts with the first action: the notice day of a contract that renews automatically, else the end.
      </p>
      {list && (
        <div className="tabs stages" role="group" aria-label="When the contract ends">
          {windowCounts(list.renewals, list.windows).map(([value, count]) => (
            <button key={String(value)} type="button" aria-pressed={chosen === value} data-testid={`renewals-${value}`} onClick={() => pick(value)}>
              {windowText(value)} ({count})
            </button>
          ))}
        </div>
      )}
      {found === undefined ? (
        <Skeleton shape="table" columns={6} />
      ) : typeof found === "string" ? (
        <p role="alert" className="error">
          {found === "session" ? (
            <>
              Your session ended.{" "}
              <button type="button" onClick={() => signOut(auth)}>
                Sign in again
              </button>
            </>
          ) : (
            <>
              {found === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
              <button type="button" onClick={() => setAttempt((n) => n + 1)}>
                Try again
              </button>
            </>
          )}
        </p>
      ) : shown.length === 0 ? (
        <p data-testid="renewals-none">No contract here.</p>
      ) : (
        <>
          <SortSelect id="renewals-select" columns={RENEWAL_COLUMNS} sort={sort} onSort={setSort} fallback="First action first" />
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Renewals">
            <table className="list" data-testid="renewal-table" data-cols="renewals-select">
              <thead>
                <tr>
                  {RENEWAL_COLUMNS.map((column) => (
                    <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortRows(shown, RENEWAL_COLUMNS, sort).map((row) => (
                  <tr key={row.id} data-testid="renewal-row" data-account={row.number}>
                    <th scope="row">
                      <a href={`${base}/accounts/${row.account_id}`} onClick={go} data-testid="renewal-link">
                        {row.number}
                      </a>
                    </th>
                    <td data-label="Name" title={row.name}>
                      <span className="clip name">{row.name}</span>
                    </td>
                    <td data-label="Ends">{dayText(row.ends_on, true)}</td>
                    <td data-label="Days left" className={row.days_left < 0 ? "num late" : "num"}>
                      {leftText(row.days_left)}
                    </td>
                    <td data-label="Renews">{row.auto_renew ? "Yes" : "No"}</td>
                    <td data-label="Notice by" className={row.notice_passed ? "late" : undefined} data-testid="renewal-notice">
                      {row.notice_by ? `${dayText(row.notice_by, true)}${row.notice_passed ? " (passed)" : ""}` : "–"}
                    </td>
                    <td data-label="Salesperson" title={row.salesperson ?? "House account"}>
                      <span className="clip name">{row.salesperson ? emailName(row.salesperson) : "House"}</span>
                    </td>
                    <td data-label="Next check-in">{row.next_check_in ? dayText(row.next_check_in, true) : "–"}</td>
                    <td data-label="Last check-in">{row.last_check_in ? dayText(row.last_check_in, true) : "–"}</td>
                    <td data-label="Version">{row.version}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </main>
  );
}
